import {
  alphaBetween, transverseSign, GEOMETRIC_TOLERANCE, BETA_ZERO_TOLERANCE,
  ALPHA0_PARALLEL_NOTE, FLAT_PARALLEL_NOTE, FLAT_INCLINED_ALERT, AXIS_PARALLEL_NOTE,
  INVISIBLE_SPL_ALERT, INVISIBLE_CROWN_ALERT,
} from "../../shared/alphaZero";
import { correctApparentDip } from "../../shared/inclination";

const TAU = 2 * Math.PI;
const EPS = 1e-10;
const radians = (angle) => angle * Math.PI / 180;
const degrees = (angle) => angle * 180 / Math.PI;
const near = (p, q) => Math.abs(p - q) <= GEOMETRIC_TOLERANCE;

/** A vertical plane on the centreline: both traces lie on the log's own lines. */
export const CIRCULAR_CENTRELINE_ALERT =
  "Discontinuity Plane coincides with Centre line of the tunnel. " +
  "Traces run along centre line at crown and centre line at invert.";

// X is positive to the right; Z is measured from the circle's lowest point.
// "Any Other Point" is a point on the face: side, offset d, height Hp above
// the invert, as in the D-shape and horseshoe.
export function circularDataPoint(dimension, R) {
  switch (dimension.orientation) {
    case "leftspl": return { X: -R, Z: R };
    case "rightspl": return { X: R, Z: R };
    case "centerline": return { X: 0, Z: 2 * R };
    case "otherpoint": {
      const d = dimension.tunnelside === "center" ? 0 : Number(dimension.distancefromcenter) || 0;
      return { X: dimension.tunnelside === "left" ? -d : d, Z: Number(dimension.verticalheight) || 0 };
    }
    default: return null;
  }
}

/**
 * Which degenerate case a plane is, or null for the main routine:
 *   "level"  — Amt = 0; the dip direction carries no meaning
 *   "alpha0" — strike parallel to the tunnel
 *   "axis"   — inclined tunnel and the apparent dip corrected for inclination
 *              comes out at 0: the plane contains the axis direction
 * The first two test whole degrees for exactly 0, so they read the rounded
 * pair; the third is a property of the plane and reads the exact one.
 */
export function circularRoute({ D, Dir, amt, I, DirExact = Dir, amtExact = amt }) {
  if (amt === 0) return "level";
  if (alphaBetween(D, Dir) === 0) return "alpha0";
  if (!I) return null;
  const alpha = alphaBetween(D, DirExact);
  const beta = degrees(Math.atan(Math.tan(radians(amtExact)) * Math.sin(radians(alpha))));
  const apDir = Math.cos(radians(DirExact - D)) >= 0 ? D : (D + 180) % 360;
  return correctApparentDip(beta, apDir, D, I).beta < BETA_ZERO_TOLERANCE ? "axis" : null;
}

/**
 * Alert for a trace that runs the whole log. A circle has no walls to coincide
 * with and no flat invert to hide a trace, so both intersections are always
 * plotted; only a plane through the log's own lines is called out.
 * (a, b) is the normal of the plane's line in the cross-section.
 */
function parallelAlert(route, a, b, anchor, R, traces) {
  if (route === "alpha0" && traces.length === 2) {
    const namedPoint = ({ X, Z }) => {
      if (near(X, R) && near(Z, R)) return "Right SPL";
      if (near(X, -R) && near(Z, R)) return "Left SPL";
      if (near(X, 0) && near(Z, 2 * R)) return "Centreline at Crown";
      if (near(X, 0) && near(Z, 0)) return "Centreline at Invert";
      return null;
    };
    // Report 22Sep26: name the data-point line first, then the other crossing.
    const ordered = [...traces].sort((p, q) =>
      Math.hypot(p.X - anchor.X, p.Z - anchor.Z) - Math.hypot(q.X - anchor.X, q.Z - anchor.Z));
    const names = ordered.map(namedPoint);
    if (names.every(Boolean) && Math.abs(a) > EPS && Math.abs(b) > EPS) {
      return "Discontinuity strike is parallel to the tunnel direction. " +
        `Trace coincides with ${names[0]} and ${names[1]}. ` +
        "The trace runs parallel to the tunnel for unlimited length. Has been clipped at the log ends.";
    }
  }
  if (Math.abs(a) < EPS) {
    if (near(anchor.Z, R)) return INVISIBLE_SPL_ALERT;
    if (near(anchor.Z, 2 * R)) return INVISIBLE_CROWN_ALERT;
  } else if (Math.abs(b) < EPS) {
    if (near(anchor.X, 0)) return CIRCULAR_CENTRELINE_ALERT;
    if (near(Math.abs(anchor.X), R)) {
      return `${ALPHA0_PARALLEL_NOTE} One trace coincides with the ${anchor.X < 0 ? "left" : "right"} SPL.`;
    }
  }
  return { level: FLAT_PARALLEL_NOTE, alpha0: ALPHA0_PARALLEL_NOTE, axis: AXIS_PARALLEL_NOTE }[route];
}

/**
 * Exact plane/circular-cylinder intersection for the planes the main routine
 * cannot slope (see circularRoute). Returns null for every other plane.
 *
 * With the plane's upward unit normal n, the tunnel axis t (positive I falls
 * along D), r across to the right and u up in the section:
 *   a (X - Xp) + b (Z - Zp) + c (RD - RD0) = 0
 *   a = n.r = sin Amt sin(Dir - D)
 *   b = n.u = sin Amt sin I cos(Dir - D) + cos Amt cos I
 *   c = n.t = sin Amt cos I cos(Dir - D) - cos Amt sin I
 * c = 0 means the plane contains the axis: its traces are the two points where
 * the line a X + b Z = const meets the circle, run along the whole log.
 * Otherwise the trace is one closed curve round the perimeter.
 *
 * Asf is |n.t| for the curve. For traces along the log it is the spacing
 * across the tunnel, sin of the line's slope in the section; a level plane
 * and a vertical one report 1.
 */
export function solveCircularAlphaZero({ D, Dir, amt, I, R, dimension, DirExact = Dir, amtExact = amt }) {
  const route = circularRoute({ D, Dir, amt, I, DirExact, amtExact });
  if (!route) return null;
  const anchor = circularDataPoint(dimension, R);
  if (!anchor || !(R > 0)) return null;
  const RD0 = Number(dimension.rd);

  let sinAmt = Math.sin(radians(amt)), cosAmt = Math.cos(radians(amt));
  let sinDelta, cosDelta;
  if (route === "level") {
    [sinAmt, cosAmt, sinDelta, cosDelta] = [0, 1, 0, 0];
  } else if (route === "alpha0") {
    [sinDelta, cosDelta] = [-(transverseSign(D, Dir) || 0), 0];
    if (amt === 90) [sinAmt, cosAmt] = [1, 0];
  } else {
    [sinAmt, cosAmt] = [Math.sin(radians(amtExact)), Math.cos(radians(amtExact))];
    [sinDelta, cosDelta] = [Math.sin(radians(DirExact - D)), Math.cos(radians(DirExact - D))];
  }
  const sinI = Math.sin(radians(I)), cosI = Math.cos(radians(I));
  const a = sinAmt * sinDelta;
  const b = sinAmt * sinI * cosDelta + cosAmt * cosI;
  const c = route === "axis" ? 0 : sinAmt * cosI * cosDelta - cosAmt * sinI;

  const alpha = route === "alpha0" ? 0 : alphaBetween(D, route === "axis" ? DirExact : Dir);
  const adip = route === "axis" ? degrees(Math.atan((sinAmt / cosAmt) * Math.sin(radians(alpha)))) : 0;
  const pointAt = (theta) => ({ X: R * Math.sin(theta), Z: R * (1 - Math.cos(theta)), Ylog: R * theta });

  if (Math.abs(c) < EPS) {
    // Project the circle centre onto the line, then move to its two crossings.
    const norm = Math.hypot(a, b);
    const nx = a / norm, nz = b / norm;
    const offset = nx * anchor.X + nz * (anchor.Z - R);
    if (Math.abs(offset) > R + EPS) return null;
    const halfChord = Math.sqrt(Math.max(0, R * R - offset * offset));
    const traces = (halfChord < EPS ? [0] : [-1, 1]).map((sign) => {
      const X = nx * offset - sign * nz * halfChord;
      const Z = R + nz * offset + sign * nx * halfChord;
      let theta = Math.atan2(X, R - Z);
      if (theta < -EPS) theta += TAU;
      return { X, Z, Ylog: R * Math.max(0, theta) };
    }).sort((p, q) => p.Ylog - q.Ylog);
    return {
      route, parallel: true, alpha, adip, adipi: 0, traces, tangent: halfChord < EPS,
      asf: route === "level" ? 1 : Math.abs(nx),
      alert: parallelAlert(route, nx, nz, anchor, R, traces),
    };
  }

  const rdAt = ({ X, Z }) => RD0 - (a * (X - anchor.X) + b * (Z - anchor.Z)) / c;
  // Include analytic extrema as well as a 0.5-degree perimeter sampling so
  // span is exact and lengths/curves do not depend on a coarse angular grid.
  const extremum = ((Math.atan2(-a, b) % TAU) + TAU) % TAU;
  const angles = [...Array.from({ length: 721 }, (_, i) => i * TAU / 720), extremum, (extremum + Math.PI) % TAU]
    .sort((x, y) => x - y);
  const points = angles.map((theta) => { const p = pointAt(theta); return { ...p, RD: rdAt(p) }; });
  const inCrown = (ylog) => ylog >= Math.PI * R / 2 - EPS && ylog <= 3 * Math.PI * R / 2 + EPS;
  let length = 0, crownLength = 0;
  points.slice(1).forEach((p, i) => {
    const prev = points[i];
    const segment = Math.hypot(p.RD - prev.RD, p.Ylog - prev.Ylog);
    length += segment;
    if (inCrown((p.Ylog + prev.Ylog) / 2)) crownLength += segment;
  });
  const spanOf = (pts) => Math.max(...pts.map((p) => p.RD)) - Math.min(...pts.map((p) => p.RD));
  return {
    route, parallel: false, alpha, adip, adipi: Math.abs(I), points, length, crownLength,
    span: spanOf(points),
    crownSpan: spanOf(points.filter((p) => inCrown(p.Ylog))),
    crossings: { invert: rdAt({ X: 0, Z: 0 }), rightSPL: rdAt({ X: R, Z: R }),
      crown: rdAt({ X: 0, Z: 2 * R }), leftSPL: rdAt({ X: -R, Z: R }) },
    asf: Math.abs(c),
    alert: route === "level" ? FLAT_INCLINED_ALERT : null,
  };
}
